import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { FaBell, FaCheckCircle, FaTimesCircle, FaPaperPlane, FaPlus } from 'react-icons/fa';
import apiService from '../services/apiService';

interface DataField {
  key: string;
  value: string;
}

const NotificationsForm: React.FC = () => {
  const [topicName, setTopicName] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dataFields, setDataFields] = useState<DataField[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [topics, setTopics] = useState<{ topicName: string; subscriberCount: number; category: string }[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(true);

  useEffect(() => {
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      setLoadingTopics(true);
      const response = await apiService.getNotificationTopics();
      console.log('Topics response:', response);
      // Ensure we always have an array
      const topicList = Array.isArray(response) ? response : [];
      setTopics(topicList);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
      setTopics([]);
    } finally {
      setLoadingTopics(false);
    }
  };

  const addDataField = () => {
    setDataFields([...dataFields, { key: '', value: '' }]);
  };

  const removeDataField = (index: number) => {
    setDataFields(dataFields.filter((_, i) => i !== index));
  };

  const updateDataField = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...dataFields];
    updated[index][field] = value;
    setDataFields(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!topicName.trim()) {
      setError('Topic name is required');
      return;
    }

    if (!title.trim()) {
      setError('Notification title is required');
      return;
    }

    if (!body.trim()) {
      setError('Notification body is required');
      return;
    }

    setIsLoading(true);

    try {
      const data: Record<string, string> = {};
      dataFields.forEach((field) => {
        if (field.key.trim() && field.value.trim()) {
          data[field.key.trim()] = field.value.trim();
        }
      });

      await apiService.sendTopicNotification({
        topicName: topicName.trim(),
        title: title.trim(),
        body: body.trim(),
        data: Object.keys(data).length > 0 ? data : undefined,
      });

      setSuccess('Notification sent successfully!');
      setTopicName('');
      setTitle('');
      setBody('');
      setDataFields([]);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send notification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setTopicName('');
    setTitle('');
    setBody('');
    setDataFields([]);
    setError('');
    setSuccess('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-primary-600 dark:to-primary-700 rounded-lg dark:rounded-xl shadow-sm dark:shadow-lg overflow-hidden border border-blue-500 dark:border-primary-500/30">
        <div className="px-6 py-8">
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 dark:bg-white/10 p-3 rounded-lg">
              <FaBell className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Send Notification</h1>
              <p className="text-blue-100 dark:text-primary-100 mt-1">
                Send push notifications to topic subscribers
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 dark:bg-success-500/20 border border-green-200 dark:border-success-500/30 rounded-lg p-4 flex items-center">
          <FaCheckCircle className="h-5 w-5 text-green-600 dark:text-success-500 mr-3" />
          <span className="text-sm text-green-800 dark:text-success-500">{success}</span>
        </div>
      )}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4 flex items-center">
          <FaTimesCircle className="h-5 w-5 text-red-600 dark:text-danger-500 mr-3" />
          <span className="text-sm text-red-800 dark:text-danger-500">{error}</span>
        </div>
      )}

      {/* Form Card */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Details</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Topic Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Topic Name <span className="text-red-500">*</span>
            </label>
            <select
              value={topicName}
              onChange={(e) => setTopicName(e.target.value)}
              disabled={loadingTopics}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="">
                {loadingTopics ? 'Loading topics...' : 'Select a topic...'}
              </option>
              {topics.map((topic, index) => (
                <option key={index} value={topic.topicName}>
                  {topic.topicName} ({topic.subscriberCount} subscribers)
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The topic name that users are subscribed to
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notification Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter notification title"
              maxLength={100}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {title.length}/100 characters
            </p>
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notification Body <span className="text-red-500">*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter notification message"
              rows={4}
              maxLength={500}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 resize-none"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {body.length}/500 characters
            </p>
          </div>

          {/* Custom Data Fields */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Custom Data (Optional)
              </label>
              <button
                type="button"
                onClick={addDataField}
                className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 dark:text-primary-400 hover:text-blue-700 dark:hover:text-primary-300 transition-colors"
              >
                <FaPlus className="h-3 w-3 mr-1" />
                Add Field
              </button>
            </div>
            {dataFields.length > 0 ? (
              <div className="space-y-3">
                {dataFields.map((field, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => updateDataField(index, 'key', e.target.value)}
                      placeholder="Key"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => updateDataField(index, 'value', e.target.value)}
                      placeholder="Value"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeDataField(index)}
                      className="p-2 text-red-500 dark:text-danger-500 hover:text-red-700 dark:hover:text-danger-400 hover:bg-red-50 dark:hover:bg-danger-500/10 rounded-lg transition-colors"
                    >
                      <FiX className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                No custom data fields. Click "Add Field" to include additional data.
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-dark-300/40">
            <button
              type="button"
              onClick={handleReset}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400/60 border border-gray-300 dark:border-dark-300/40 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300/60 transition-colors disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-primary-600 rounded-lg hover:bg-blue-700 dark:hover:bg-primary-700 transition-colors disabled:opacity-50 shadow-sm"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <FaPaperPlane className="h-4 w-4 mr-2" />
                  Send Notification
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NotificationsForm;
